import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CheckSetup,
  CheckSetupInput,
  CheckStockLayout,
  CheckStockLayoutStatus,
  ConfirmPrintRunInput,
  MicrFontStatus,
  MicrSymbols,
  PrintQueue,
  PrintRun,
  SpoiledCheckNumber,
} from '@accounting/shared';
import { api, postForBlob } from '../lib/api';

export const printQueueKey = ['check-printing', 'queue'] as const;
export const checkSetupsKey = ['check-printing', 'setups'] as const;
export const micrFontKey = ['check-printing', 'micr-font'] as const;
export const printRunsKey = ['check-printing', 'runs'] as const;
export const stockLayoutKey = ['check-printing', 'stock-layout'] as const;

/**
 * Invalidate everything a print run touches. Numbers move, the queue drains,
 * balances do not change (the entries were posted when the checks were written)
 * but the register and the sidebar badge both do.
 */
function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['check-printing'] });
  qc.invalidateQueries({ queryKey: ['checks'] });
  qc.invalidateQueries({ queryKey: ['nudges'] });
}

// ── Queue ────────────────────────────────────────────────────────────────────

/** Omit checksPerSheet to use however many faces the configured stock holds. */
export function usePrintQueue(checksPerSheet?: number) {
  return useQuery({
    queryKey: [...printQueueKey, checksPerSheet ?? 'stock'],
    queryFn: () =>
      api
        .get<{ queue: PrintQueue }>(
          `/check-printing/queue${checksPerSheet ? `?checksPerSheet=${checksPerSheet}` : ''}`,
        )
        .then((r) => r.queue),
  });
}

export interface PrintResult {
  runId: string;
  checkCount: number;
  sheetCount: number;
  objectUrl: string;
}

export function usePrintChecks() {
  const qc = useQueryClient();
  return useMutation<PrintResult, Error, { checkIds?: string[]; checksPerSheet?: number }>({
    mutationFn: async (input) => {
      const { objectUrl, headers } = await postForBlob('/check-printing/print', input);
      return {
        runId: headers.get('X-Print-Run-Id') ?? '',
        checkCount: Number(headers.get('X-Print-Run-Checks') ?? 0),
        sheetCount: Number(headers.get('X-Print-Run-Sheets') ?? 0),
        objectUrl,
      };
    },
    onSuccess: () => invalidateAll(qc),
  });
}

// ── Runs ─────────────────────────────────────────────────────────────────────

export function usePrintRuns(status?: PrintRun['status']) {
  return useQuery({
    queryKey: [...printRunsKey, 'list', status ?? 'all'],
    queryFn: () =>
      api
        .get<{ runs: PrintRun[] }>(
          `/check-printing/runs${status ? `?status=${status}` : ''}`,
        )
        .then((r) => r.runs),
  });
}

/**
 * Runs that were printed but never confirmed — numbers assigned, nothing
 * released. Surfaced on the queue page so a closed tab or a lost response
 * cannot strand checks with no way back to them.
 */
export function usePendingPrintRuns() {
  return usePrintRuns('pending');
}

export function usePrintRun(runId: string | null) {
  return useQuery({
    queryKey: [...printRunsKey, runId ?? 'none'],
    enabled: !!runId,
    queryFn: () =>
      api.get<{ run: PrintRun }>(`/check-printing/runs/${runId}`).then((r) => r.run),
  });
}

export function useConfirmPrintRun() {
  const qc = useQueryClient();
  return useMutation<PrintRun, Error, { runId: string } & ConfirmPrintRunInput>({
    mutationFn: ({ runId, ...body }) =>
      api
        .post<{ run: PrintRun }>(`/check-printing/runs/${runId}/confirm`, body)
        .then((r) => r.run),
    onSuccess: () => invalidateAll(qc),
  });
}

// ── Setup ────────────────────────────────────────────────────────────────────

export function useCheckSetups() {
  return useQuery({
    queryKey: checkSetupsKey,
    queryFn: () =>
      api.get<{ setups: CheckSetup[] }>('/check-printing/setups').then((r) => r.setups),
  });
}

export function useUpdateCheckSetup() {
  const qc = useQueryClient();
  return useMutation<
    CheckSetup,
    Error,
    { companyId: string; bankAccountId: string; input: CheckSetupInput }
  >({
    mutationFn: ({ companyId, bankAccountId, input }) =>
      api
        .put<{ setup: CheckSetup }>(`/companies/${companyId}/check-setup/${bankAccountId}`, input)
        .then((r) => r.setup),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUploadSignature() {
  const qc = useQueryClient();
  return useMutation<
    CheckSetup,
    Error,
    { companyId: string; bankAccountId: string; imageBase64: string; fileName: string }
  >({
    mutationFn: ({ companyId, bankAccountId, imageBase64, fileName }) =>
      api
        .post<{ setup: CheckSetup }>(
          `/companies/${companyId}/check-setup/${bankAccountId}/signature`,
          { imageBase64, fileName },
        )
        .then((r) => r.setup),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteSignature() {
  const qc = useQueryClient();
  return useMutation<CheckSetup, Error, { companyId: string; bankAccountId: string }>({
    mutationFn: ({ companyId, bankAccountId }) =>
      api
        .delete<{ setup: CheckSetup }>(
          `/companies/${companyId}/check-setup/${bankAccountId}/signature`,
        )
        .then((r) => r.setup),
    onSuccess: () => invalidateAll(qc),
  });
}

// ── Check stock layout ───────────────────────────────────────────────────────

export function useCheckStockLayout() {
  return useQuery({
    queryKey: stockLayoutKey,
    queryFn: () =>
      api
        .get<{ stock: CheckStockLayoutStatus }>('/check-printing/stock-layout')
        .then((r) => r.stock),
  });
}

export function useUpdateCheckStockLayout() {
  const qc = useQueryClient();
  return useMutation<CheckStockLayoutStatus, Error, CheckStockLayout>({
    mutationFn: (layout) =>
      api
        .put<{ stock: CheckStockLayoutStatus }>('/check-printing/stock-layout', layout)
        .then((r) => r.stock),
    onSuccess: () => invalidateAll(qc),
  });
}

// ── Blank checks (offsite) ───────────────────────────────────────────────────

export interface BlankCheckResult {
  numbers: string[];
  objectUrl: string;
}

export function usePrintBlankChecks() {
  const qc = useQueryClient();
  return useMutation<
    BlankCheckResult,
    Error,
    { companyId: string; bankAccountId: string; count: number; notes?: string }
  >({
    mutationFn: async ({ companyId, bankAccountId, count, notes }) => {
      const { objectUrl, headers } = await postForBlob(
        `/companies/${companyId}/check-setup/${bankAccountId}/blank-checks`,
        { count, notes },
      );
      const raw = headers.get('X-Blank-Check-Numbers') ?? '';
      return { numbers: raw ? raw.split(',') : [], objectUrl };
    },
    onSuccess: () => invalidateAll(qc),
  });
}

export function useOpenBlankChecks(companyId: string | null) {
  return useQuery({
    queryKey: ['check-printing', 'blank-checks', companyId ?? 'none'],
    enabled: companyId !== null,
    queryFn: () =>
      api
        .get<{ blanks: { id: string; checkNumber: string; createdAt: string; notes: string | null }[] }>(
          `/companies/${companyId}/blank-checks`,
        )
        .then((r) => r.blanks),
  });
}

// ── MICR font ────────────────────────────────────────────────────────────────

export function useMicrFont() {
  return useQuery({
    queryKey: micrFontKey,
    queryFn: () =>
      api.get<{ font: MicrFontStatus }>('/check-printing/micr-font').then((r) => r.font),
  });
}

export function useUploadMicrFont() {
  const qc = useQueryClient();
  return useMutation<MicrFontStatus, Error, { fontBase64: string; fileName: string }>({
    mutationFn: (body) =>
      api.post<{ font: MicrFontStatus }>('/check-printing/micr-font', body).then((r) => r.font),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateMicrSymbols() {
  const qc = useQueryClient();
  return useMutation<MicrFontStatus, Error, MicrSymbols>({
    mutationFn: (symbols) =>
      api
        .put<{ font: MicrFontStatus }>('/check-printing/micr-font/symbols', symbols)
        .then((r) => r.font),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteMicrFont() {
  const qc = useQueryClient();
  return useMutation<MicrFontStatus, Error, void>({
    mutationFn: () =>
      api.delete<{ font: MicrFontStatus }>('/check-printing/micr-font').then((r) => r.font),
    onSuccess: () => invalidateAll(qc),
  });
}

// ── Spoiled numbers ──────────────────────────────────────────────────────────

export function useSpoiledNumbers(companyId: string | null) {
  return useQuery({
    queryKey: ['check-printing', 'spoiled', companyId ?? 'none'],
    enabled: companyId !== null,
    queryFn: () =>
      api
        .get<{ spoiled: SpoiledCheckNumber[] }>(`/companies/${companyId}/check-spoiled-numbers`)
        .then((r) => r.spoiled),
  });
}
